import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

/**
 * A small per-key request cache backed by shareReplay(1), used in place of the ts-cacheable
 * @Cacheable decorator. Concurrent callers for the same key (e.g. a route title resolver and the
 * page component it labels) share one in-flight/most-recent request instead of each issuing their
 * own. The gain over the decorator is that the cache is an ordinary object: you can see what it
 * holds, clear a single key from code, and read the eviction rule without decorator metadata.
 *
 * Pass maxAgeMs for the same sliding-expiration behavior @Cacheable({ maxAge, slidingExpiration })
 * provided - every read within the window both serves the cached value and pushes the window out
 * again. Omit it (or pass null) to cache indefinitely until clear() is called, which is the right
 * shape when invalidation is driven by an existing save/cache-buster signal instead of time.
 *
 * Pass bustOn$ (normally ts-cacheable's globalCacheBusterNotifier) for anything whose response is
 * stamped from mutable session state - the active CSO above all. A time window alone cannot cover
 * that: the state can change inside the window, and every hit slides the window further out. This
 * is the equivalent of @Cacheable's cacheBusterObserver, and any cache holding CSO-scoped data
 * needs it. The subscription is never torn down; these caches are owned by app-lifetime services
 * and the notifier lives as long as the app.
 */
export class TtlRequestCache<TKey, TValue> {
  private readonly entries = new Map<TKey, { request$: Observable<TValue>; cachedAt: number }>();

  constructor(
    private readonly maxAgeMs: number | null = null,
    bustOn$?: Observable<unknown>,
  ) {
    bustOn$?.subscribe(() => this.clear());
  }

  get(key: TKey, factory: () => Observable<TValue>): Observable<TValue> {
    const existing = this.entries.get(key);
    if (existing && (this.maxAgeMs === null || Date.now() - existing.cachedAt < this.maxAgeMs)) {
      existing.cachedAt = Date.now(); // sliding expiration
      return existing.request$;
    }

    const request$ = factory().pipe(shareReplay({ bufferSize: 1, refCount: false }));
    this.entries.set(key, { request$, cachedAt: Date.now() });
    return request$;
  }

  /** Clears one entry, or the whole cache when called with no key. */
  clear(key?: TKey): void {
    if (key === undefined) {
      this.entries.clear();
    } else {
      this.entries.delete(key);
    }
  }
}
